// Sentry browser SDK init. Imported FIRST in src/index.js so it initializes
// before the app renders. DSN comes from REACT_APP_SENTRY_DSN (CRA inlines
// REACT_APP_* at build time) — when unset, Sentry.init no-ops, so local dev
// without a DSN is fine.
import * as Sentry from '@sentry/react';

import { scrubSignTokens } from './lib/scrubSignTokens';

const ENV = process.env.NODE_ENV || 'development';
const isProd = ENV === 'production';

// Signing links are keys (Nic 2026-10-24): events are scrubbed of them
// (lib/scrubSignTokens), and a session that opens on a signing link records
// no replay at all (a replay keeps the page's address).
const onSigningPage =
  typeof window !== 'undefined' && /^\/sign\//.test(window.location.pathname || '');

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: ENV,
  // release is wired later alongside source-map upload (Sentry wizard).

  integrations: [
    Sentry.browserTracingIntegration(),
    ...(onSigningPage
      ? []
      : [
          Sentry.replayIntegration({
            // PHI/PII protection — this is a veteran-claims app, so the DOM contains
            // names, SSNs, ratings, etc. Mask aggressively so replays never capture
            // protected data. Do NOT relax these without a privacy review.
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
          }),
        ]),
  ],
  beforeSend: (event) => scrubSignTokens(event),
  beforeSendTransaction: (event) => scrubSignTokens(event),
  beforeBreadcrumb: (crumb) => scrubSignTokens(crumb),

  // Performance tracing — full in dev, sampled in prod.
  tracesSampleRate: isProd ? 0.1 : 1.0,
  // Only attach tracing headers to our own API (avoids CORS noise on 3rd parties).
  tracePropagationTargets: ['localhost', /^https:\/\/app\.veritasratings\.com/],

  // Session Replay — sample a slice of normal sessions, but capture every
  // session that hits an error. Safe to record because of the masking above.
  replaysSessionSampleRate: isProd ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0,

  // Structured logs via Sentry.logger.*
  enableLogs: true,

  // Collect default PII (IP, etc.). Replay masking above covers the DOM; revisit
  // with a privacy pass before relying on this in a HIPAA context.
  sendDefaultPii: true,
});

export default Sentry;
